// ----------------------------------------------------------------------------
// Collection filters
// ----------------------------------------------------------------------------
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------
import '../../snippets/_containers/modal/modal';
import iamotaEventHandler from 'assets/scripts/helpers/event-handler';

// Styles
import './_collection-filters-sort.scss';


// -------------------------------------
// Variables
// -------------------------------------
// Selectors
const FILTER_WRAPPER          = '#FiltersWrapperDesktop';
const PRODUCT_COUNT           = '#ProductCount';
const PRODUCT_GRID_CONTAINER  = '#ProductGridContainer';
const PRODUCT_GRID            = '#ProductGrid';
const FILTER_DETAILS_ELEMENTS = '#FiltersForm .js--filter, #FiltersFormMobile .js--filter';
const FILTER_ELEMENT          = '.js--filter';
const ACTIVE_FILTER_MOBILE    = '.js--active-filters-mobile';
const ACTIVE_FILTER_DESKTOP   = '.js--active-filters-desktop';
const FILTERS_SELECTED        = '.js--filters__selected';
const FILTERS_SUMMARY         = '.js--filters__summary';
const SORT_SELECTOR           = '.collection-sort__selector';
const FILTERS_FORM_SELECTOR   = '#FiltersForm';

// Classes, IDs & Other
const FILTERS_FORM_ID      = 'FiltersForm';
const SORT_FORM_ID_DESKTOP = 'SortForm';
const SORT_FORM_ID_MOBILE  = 'SortFormMobile';
const LOADING_CLASS        = 'loading';
const CLOSED_FILTERS_CLASS = 'closed-filters--mobile';
const OPEN_ATTR            = 'open';


// -------------------------------------
// Hooks
// -------------------------------------

// Class 'FiltersForm' to handle filter functionalities
class FiltersForm extends HTMLElement {
    constructor() {
        // Initializing event handlers and methods for filtering
        super();
        this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

        // Debouncing the submit handler to avoid frequent submissions
        this.debouncedOnSubmit = debounce((event) => {
            this.onSubmitHandler(event);
        }, 500);

        // Adding event listeners for filter form input and keyup actions
        const filterForm = this.querySelector('form');
        filterForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

        // Adding an event listener for the escape key to close details elements
        const filterWrapper = this.querySelector(FILTER_WRAPPER);
        if (filterWrapper) filterWrapper.addEventListener('keyup', onKeyUpEscape);
    }

    // Static method to set listeners for popstate events
    static setListeners() {
        const onHistoryChange = (event) => {
            const searchParams = event.state ? event.state.searchParams : FiltersForm.searchParamsInitial;
            if (searchParams === FiltersForm.searchParamsPrev) return;
            FiltersForm.renderPage(searchParams, null, false);
        };
        window.addEventListener('popstate', onHistoryChange);
    }

    // Static method to render the page with updated filter parameters
    static renderPage(searchParams, event, updateURLHash = true) {
        // Implementation details for rendering different sections of the page
        FiltersForm.searchParamsPrev = searchParams;
        const sections = FiltersForm.getSections();
        const countContainer = document.querySelector(PRODUCT_COUNT);
        document.querySelector(PRODUCT_GRID_CONTAINER).querySelector('.collection').classList.add(LOADING_CLASS);

        if (countContainer) {
            countContainer.classList.add(LOADING_CLASS);
        }

        sections.forEach((section) => {
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
            const filterDataUrl = (element) => element.url === url;

            FiltersForm.filterData.some(filterDataUrl)
                ? FiltersForm.renderSectionFromCache(filterDataUrl, event)
                : FiltersForm.renderSectionFromFetch(url, event);
        });

        // Updating the URL hash if required
        if (updateURLHash) FiltersForm.updateURLHash(searchParams);
    }

    static renderSectionFromFetch(url, event) {
        fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
                const html = responseText;
                FiltersForm.filterData = [...FiltersForm.filterData, { html, url }];
                FiltersForm.renderFilters(html, event);
                FiltersForm.renderProductGridContainer(html);
                FiltersForm.renderProductCount(html);
                FiltersForm.renderNoResultsMessage(html);
            });
    }

    static renderSectionFromCache(filterDataUrl, event) {
        const html = FiltersForm.filterData.find(filterDataUrl).html;
        FiltersForm.renderFilters(html, event);
        FiltersForm.renderProductGridContainer(html);
        FiltersForm.renderProductCount(html);
        FiltersForm.renderNoResultsMessage(html);
    }

    static renderProductGridContainer(html) {
        document.querySelector(PRODUCT_GRID_CONTAINER).innerHTML = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector(PRODUCT_GRID_CONTAINER).innerHTML;
    }


    static renderProductCount(html) {
        const countContainers = document.querySelectorAll(PRODUCT_COUNT);
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const counts = parsedHTML.querySelectorAll(PRODUCT_COUNT);

        if (countContainers) {
            countContainers.forEach((container, index) => {
                if (counts[index]) {
                    // Update innerHTML
                    container.innerHTML = counts[index].innerHTML;

                    // Update data-product-count attribute
                    const dataProductCount = counts[index].getAttribute('data-product-count');
                    container.setAttribute('data-product-count', dataProductCount);

                    container.classList.remove(LOADING_CLASS);
                }
            });
        }

        // Render product results count for Search Page
        const productResultsCountContainers = document.querySelectorAll('#ProductResultsCount');
        const productResultsCount = parsedHTML.querySelector('#ProductResultsCount');
        if (productResultsCountContainers && productResultsCount) {
            productResultsCountContainers.forEach((container) => {
                // Update innerHTML
                container.innerHTML = productResultsCount.innerHTML;
            });
        }
    }

    static renderNoResultsMessage() {
        const countContainers = document.querySelector(PRODUCT_COUNT);
        const productCountValue = countContainers.getAttribute('data-product-count');

        if( productCountValue ) {
            if(productCountValue === '0') {
                document.querySelector(PRODUCT_GRID).innerHTML = '<p class="no-results-text">' + window.translations.noResultsFound + '</p>';
            }
        }
    }

    static renderFilters(html) {
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const filterDetailsElements = parsedHTML.querySelectorAll(FILTER_DETAILS_ELEMENTS);
        const filtersToRender = Array.from(filterDetailsElements);

        filtersToRender.forEach((element) => {
            document.querySelector(`${FILTER_ELEMENT}[data-index="${element.dataset.index}"]`).className = element.classList;
            document.querySelector(`${FILTER_ELEMENT}[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
        });

        FiltersForm.renderActiveFilters(parsedHTML);
    }

    static renderActiveFilters(html) {
        const activeFilterElementSelectors = [ACTIVE_FILTER_MOBILE, ACTIVE_FILTER_DESKTOP];

        activeFilterElementSelectors.forEach((selector) => {
            const activeFiltersElement = html.querySelector(selector);
            if (!activeFiltersElement) return;
            document.querySelector(selector).innerHTML = activeFiltersElement.innerHTML;
        });
    }

    static renderCounts(source, target) {
        const targetElement = target.querySelector(FILTERS_SELECTED);
        const sourceElement = source.querySelector(FILTERS_SELECTED);

        const targetElementAccessibility = target.querySelector(FILTERS_SUMMARY);
        const sourceElementAccessibility = source.querySelector(FILTERS_SUMMARY);

        if (sourceElement && targetElement) {
            target.querySelector(FILTERS_SELECTED).outerHTML = source.querySelector(FILTERS_SELECTED).outerHTML;
        }

        if (targetElementAccessibility && sourceElementAccessibility) {
            target.querySelector(FILTERS_SUMMARY).outerHTML = source.querySelector(FILTERS_SUMMARY).outerHTML;
        }
    }

    static updateURLHash(searchParams) {
        history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);

        iamotaEventHandler.triggerEvent('filterPramsUpdated', {filterParams: searchParams});
    }

    static getSections() {
        return [
            {
                section: document.querySelector(PRODUCT_GRID).dataset.id,
            },
        ];
    }

    // Handling event changes for sorting selectors
    connectedCallback() {
        const sortSelectors = document.querySelectorAll(SORT_SELECTOR);

        if (sortSelectors.length > 0) {
            sortSelectors.forEach((sortSelector) => {
                sortSelector.addEventListener('change', this.handleSelectorChange.bind(this));
            });
        }
    }

    // Event handler for sorting selector changes
    handleSelectorChange(event) {
        const sortSelectors = document.querySelectorAll(SORT_SELECTOR);
        const selectedValue = event.target.value;

        // Remove the event listener from the current element
        event.target.removeEventListener('change', this.handleSelectorChange);
        sortSelectors.forEach((otherSelector) => {
            if (otherSelector !== event.target) {
                otherSelector.value = selectedValue;
            }
        });

        event.target.addEventListener('change', this.handleSelectorChange.bind(this));
    }

    // Method to create search parameters from form data
    createSearchParams(form) {
        const formData = new FormData(form);
        return new URLSearchParams(formData).toString();
    }

    // Event handler for form submissions
    onSubmitForm(searchParams, event) {
        FiltersForm.renderPage(searchParams, event);
    }

    // Event handler for form input changes
    onSubmitHandler(event) {
        event.preventDefault();
        const sortFilterForms = document.querySelectorAll('filters-form form');

        const forms = [];
        const isMobile = event.target.closest('form').id === SORT_FORM_ID_MOBILE;

        sortFilterForms.forEach((form) => {
            if (!isMobile) {
                if ( form.id === SORT_FORM_ID_DESKTOP || form.id === FILTERS_FORM_ID ) {
                    forms.push(this.createSearchParams(form));
                }
            } else if (form.id === SORT_FORM_ID_MOBILE || form.id === FILTERS_FORM_ID ) {
                forms.push(this.createSearchParams(form));
            }
        });
        this.onSubmitForm(forms.join('&'), event);
    }

    // Event handler for active filter clicks
    onActiveFilterClick(event) {
        event.preventDefault();
        const url =
            event.currentTarget.href.indexOf('?') == -1
                ? ''
                : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
        FiltersForm.renderPage(url);
    }
}

FiltersForm.filterData = [];
FiltersForm.searchParamsInitial = window.location.search.slice(1);
FiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('filters-form', FiltersForm);
FiltersForm.setListeners();

// Reset the price range to the minimum and maximum
// eslint-disable-next-line no-unused-vars
class PriceRange extends HTMLElement {
    constructor() {
        super();
        this.querySelectorAll('input').forEach((element) =>
            element.addEventListener('change', this.onRangeChange.bind(this))
        );
        this.setMinAndMaxValues();
    }

    onRangeChange(event) {
        this.adjustToValidValues(event.currentTarget);
        this.setMinAndMaxValues();
    }

    setMinAndMaxValues() {
        const inputs = this.querySelectorAll('input');
        const minInput = inputs[0];
        const maxInput = inputs[1];
        if (maxInput.value) minInput.setAttribute('max', maxInput.value);
        if (minInput.value) maxInput.setAttribute('min', minInput.value);
        if (minInput.value === '') maxInput.setAttribute('min', 0);
        if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
    }

    adjustToValidValues(input) {
        const value = Number(input.value);
        const min = Number(input.getAttribute('min'));
        const max = Number(input.getAttribute('max'));

        if (value < min) input.value = min;
        if (value > max) input.value = max;
    }
}
// Uncomment to enable this feature
// customElements.define('price-range', PriceRange);

class FilterRemove extends HTMLElement {
    constructor() {
        super();
        const filterLink = this.querySelector('a');
        filterLink.setAttribute('role', 'button');
        filterLink.addEventListener('click', this.closeFilter.bind(this));
        filterLink.addEventListener('keyup', (event) => {
            event.preventDefault();
            if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
        });
    }

    closeFilter(event) {
        event.preventDefault();
        const form = this.closest('filters-form') || document.querySelector('filters-form');
        form.onActiveFilterClick(event);
    }
}
customElements.define('filter-remove', FilterRemove);

// -------------------------------------
// Helpers
// -------------------------------------

// Limit the frequency of a function's execution
function debounce(fn, wait) {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
    };
}

// Function to handle keyup 'Escape' event to close details element
function onKeyUpEscape(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    const summaryElement = openDetailsElement.querySelector('summary');
    openDetailsElement.removeAttribute('open');
    summaryElement.setAttribute('aria-expanded', false);
    summaryElement.focus();
}

function toggleFilters(action) {
    const filtersForm = document.querySelector(FILTERS_FORM_SELECTOR);
    const filters = filtersForm ? filtersForm.querySelectorAll(FILTER_ELEMENT) : [];

    if (filtersForm && filtersForm.classList.contains(CLOSED_FILTERS_CLASS) && filters.length > 0) {
        filters.forEach(function (filter) {
            if (filter && (action === 'open' ? !filter.hasAttribute(OPEN_ATTR) : filter.hasAttribute(OPEN_ATTR))) {
                action === 'open' ? filter.setAttribute(OPEN_ATTR, true) : filter.removeAttribute(OPEN_ATTR);
            }
        });
    }
}

function handleFiltersByViewport() {
    const breakpoint = window.matchMedia('(max-width: 1023px)');
    const action = breakpoint.matches ? 'close' : 'open';

    toggleFilters(action);
}

function initializeTabs() {
    const tabs = document.querySelectorAll('.tablinks');
    const contents = document.querySelectorAll('.tabcontent');

    tabs.forEach(tab => {
        tab.addEventListener('click', function() {
            // Remove 'active' class from all tabs
            tabs.forEach(tab => tab.classList.remove('active'));
            // Add 'active' class to the clicked tab
            this.classList.add('active');
            
            // Hide all content
            contents.forEach(content => content.style.display = 'none');
            
            // Show the corresponding content
            const tabName = this.getAttribute('data-tab');
            document.getElementById(tabName).style.display = 'block';
        });
    });
}

// Selector Width

document.addEventListener('DOMContentLoaded', function() {
    // resize on initial load
    var sortSelects = document.querySelectorAll('.collection-sort__selector');

    if (sortSelects) {

        sortSelects.forEach((select) => {
            resizeSelect(select);
            select.addEventListener('change', (e) => {
                resizeSelect(e.target);
            });
        });
    }
});

function resizeSelect(sel) {
    let tempOption = document.createElement('option');
    tempOption.textContent = sel.selectedOptions[0].textContent;

    let tempSelect = document.createElement('select');
    tempSelect.style.visibility = 'hidden';
    tempSelect.style.position = 'fixed';
    tempSelect.appendChild(tempOption);
    
    sel.after(tempSelect);
    sel.style.width = `${+tempSelect.clientWidth}px`;
    tempSelect.remove();
}

document.addEventListener('DOMContentLoaded', () => {
    // Select all `.search-results__content` elements
    const contents = document.querySelectorAll('.search-results__content');

    contents.forEach(content => {
        // Check if the content does not contain an image
        if (!content.querySelector('.search-results__image')) {
            // Apply a CSS class to change flex-direction
            content.classList.add('flex-column');
        }
    });
});



// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', initializeTabs);
document.addEventListener('DOMContentLoaded', handleFiltersByViewport);
window.addEventListener('resize', handleFiltersByViewport);
